import { useCallback } from 'react';
import { Plus, Trash2, GripVertical, ExternalLink } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import type { LinkResourceFormItem } from '../types/expert-tip';

interface LinkResourcesEditorProps {
  value: LinkResourceFormItem[];
  onChange: (resources: LinkResourceFormItem[]) => void;
  error?: string;
}

function getFaviconUrl(url: string): string | null {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
}

export default function LinkResourcesEditor({
  value,
  onChange,
  error,
}: LinkResourcesEditorProps) {
  const handleAdd = useCallback(() => {
    onChange([
      ...value,
      { title: '', url: '', sortOrder: value.length },
    ]);
  }, [value, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      const updated = value.filter((_, i) => i !== index);
      onChange(updated.map((lr, i) => ({ ...lr, sortOrder: i })));
    },
    [value, onChange]
  );

  const handleChange = useCallback(
    (index: number, field: keyof LinkResourceFormItem, fieldValue: string) => {
      const updated = value.map((lr, i) =>
        i === index ? { ...lr, [field]: fieldValue } : lr
      );
      onChange(updated);
    },
    [value, onChange]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Link Resources</Label>
        <span className="text-xs text-admin-slate-500">
          {value.length} link{value.length !== 1 ? 's' : ''}
        </span>
      </div>

      {value.length === 0 ? (
        <div className="rounded-lg border border-dashed border-admin-slate-300/20 p-6 text-center">
          <ExternalLink className="w-8 h-8 text-admin-slate-500 mx-auto mb-2" />
          <p className="text-sm text-admin-slate-500">No link resources yet</p>
          <p className="text-xs text-admin-slate-500/70 mt-1">
            Add links to external articles, videos, or resources
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {value.map((resource, index) => {
            const faviconUrl = resource.url ? getFaviconUrl(resource.url) : null;

            return (
              <div
                key={index}
                className="group relative rounded-lg border border-admin-slate-300/15 bg-admin-dark-800/50 p-4 transition-colors hover:border-admin-slate-300/25"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-admin-slate-500/50" />
                    <span className="text-xs font-medium text-admin-accent-blue">
                      Link {index + 1}
                    </span>
                    {faviconUrl && (
                      <img
                        src={faviconUrl}
                        alt=""
                        className="w-4 h-4 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="p-1 rounded text-admin-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
                    title="Remove link"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-admin-slate-500">Title</label>
                    <Input
                      value={resource.title}
                      onChange={(e) => handleChange(index, 'title', e.target.value)}
                      placeholder="e.g. Research on vocabulary development"
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-admin-slate-500">URL</label>
                    <Input
                      value={resource.url}
                      onChange={(e) => handleChange(index, 'url', e.target.value)}
                      placeholder="https://example.com/article"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={handleAdd}
        className="flex items-center gap-2 px-4 py-2 w-full justify-center rounded-lg border border-dashed border-admin-accent-blue/30 text-admin-accent-blue hover:bg-admin-accent-blue/5 hover:border-admin-accent-blue/50 transition-colors text-sm font-medium"
      >
        <Plus className="w-4 h-4" />
        Add Link Resource
      </button>

      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}
